import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Bug, AlertCircle } from "lucide-react";

// ============================================================================
// TYPES
// ============================================================================

type Niche = "BeauteSoins" | "ElectroniqueAccessoires" | "MaisonCuisine" | "ComplementsAlimentaires" | "Mode";
type Concurrence = "Forte" | "Moderee" | "Faible" | "BlueOcean";
type BudgetStage = "Minimal" | "Recommande" | "Optimal";
type Saison = "Normale" | "Pic" | "Creuse";

interface Contexte {
  marqueConnue: boolean;
  niche: Niche;
  concurrence: Concurrence;
  budgetStage: BudgetStage;
  saison: Saison;
}

interface Optimizer {
  prixTTC: number;
  aov: number;
  cvrCat: number;
  cpcCat: number;
  acosCible: number;
  tacosCible: number;
  stockJours: number;
  brandStorePret: boolean;
}

interface Retail {
  buyBoxPct: number;
  priceIndex: number;
  rating: number;
  reviews: number;
  images: number;
  hasAplus: boolean;
}

interface Placements {
  sosACoS: number;
  sosCVR: number;
  rosACoS: number;
  ppACoS: number;
}

interface Promo {
  active: boolean;
  type: string;
  dateDebut: string;
  dateFin: string;
}

interface Daypart {
  spendNightShare: number;
  cvrNightRel: number;
}

interface Reco {
  budget: {
    budgetJour: number;
    repartition: { sp: number; sb: number; sd: number };
    exp: string;
  };
  sp: {
    bidInit: number;
    seuilClics: number;
    kwExact: number;
    kwPhrase: number;
    asinPT: number;
    budParAG: number;
  };
}

interface DebugPanelProps {
  contexte: Contexte;
  optimizer: Optimizer;
  retail: Retail;
  placements: Placements;
  promo: Promo;
  daypart: Daypart;
  reco: Reco;
  onContexteChange: (field: keyof Contexte, value: any) => void;
  onOptimizerChange: (field: keyof Optimizer, value: any) => void;
  onPromoChange: (field: keyof Promo, value: any) => void;
  onReset: () => void;
}

// ============================================================================
// CONSTANTES
// ============================================================================

const NICHES: Record<Niche, { cpcRef: number; cvrRef: number; tacosRef: number }> = {
  BeauteSoins: { cpcRef: 0.90, cvrRef: 0.10, tacosRef: 0.12 },
  ElectroniqueAccessoires: { cpcRef: 0.70, cvrRef: 0.06, tacosRef: 0.09 },
  MaisonCuisine: { cpcRef: 0.60, cvrRef: 0.08, tacosRef: 0.10 },
  ComplementsAlimentaires: { cpcRef: 1.20, cvrRef: 0.07, tacosRef: 0.16 },
  Mode: { cpcRef: 0.45, cvrRef: 0.03, tacosRef: 0.13 },
};

const CADRES_BUDGET: Record<BudgetStage, { min: number; max: number; repartition: { sp: number; sb: number; sd: number }; ordersCible: number }> = {
  Minimal: { min: 50, max: 80, repartition: { sp: 0.70, sb: 0.25, sd: 0.05 }, ordersCible: 5 },
  Recommande: { min: 120, max: 200, repartition: { sp: 0.65, sb: 0.25, sd: 0.10 }, ordersCible: 10 },
  Optimal: { min: 250, max: 500, repartition: { sp: 0.60, sb: 0.25, sd: 0.15 }, ordersCible: 20 },
};

const REP_SP: Record<Concurrence, { auto: number; exact: number; phrase: number; product: number }> = {
  Forte: { auto: 0.30, exact: 0.35, phrase: 0.20, product: 0.15 },
  Moderee: { auto: 0.25, exact: 0.40, phrase: 0.20, product: 0.15 },
  Faible: { auto: 0.20, exact: 0.35, phrase: 0.25, product: 0.20 },
  BlueOcean: { auto: 0.20, exact: 0.30, phrase: 0.30, product: 0.20 },
};

const MULTIPLICATEURS = {
  ConcurrenceForte: 1.20,
  ConcurrenceModeree: 1.00,
  ConcurrenceFaible: 0.85,
  BlueOcean: 0.70,
  MarqueConnue: 0.90,
  SaisonPic: 1.25,
  SaisonCreuse: 0.85,
  PromoActive: 1.15,
};

// ============================================================================
// UTILITAIRES
// ============================================================================

const borne = (min: number, max: number, x: number): number => Math.max(min, Math.min(max, x));
const arrondi = (x: number): number => Math.round(x);
const valOr = (v: number | undefined, def: number): number => (v !== undefined && v > 0 ? v : def);

// ============================================================================
// CALCUL DEBUG
// ============================================================================

interface DebugCalcs {
  clicks: number;
  budgetClicks: number;
  budgetTacos: number;
  m: number;
  budgetTheo: number;
  budgetJour: number;
  budSP: number;
  budSB: number;
  budSD: number;
  bidInit: number;
  seuilClics: number;
  kwExact: number;
  kwPhrase: number;
  asinPT: number;
  budParAG: number;
  lockReason: string;
}

const computeDebug = (ctx: Contexte, eco: Optimizer, ret: Retail, pr: Promo): DebugCalcs => {
  const ref = NICHES[ctx.niche];
  const cvrEff = valOr(eco.cvrCat, ref.cvrRef);
  const cpcEff = valOr(eco.cpcCat, ref.cpcRef);
  const tacosEff = valOr(eco.tacosCible, ref.tacosRef);
  const aovEff = valOr(eco.aov, eco.prixTTC);
  const orders = CADRES_BUDGET[ctx.budgetStage].ordersCible;
  const clicks = orders / Math.max(cvrEff, 0.01);
  const budgetClicks = clicks * cpcEff;
  const budgetTacos = tacosEff * orders * aovEff;

  let m = 1.0;
  if (ctx.concurrence === "Forte") m *= MULTIPLICATEURS.ConcurrenceForte;
  if (ctx.concurrence === "Moderee") m *= MULTIPLICATEURS.ConcurrenceModeree;
  if (ctx.concurrence === "Faible") m *= MULTIPLICATEURS.ConcurrenceFaible;
  if (ctx.concurrence === "BlueOcean") m *= MULTIPLICATEURS.BlueOcean;
  if (ctx.marqueConnue) m *= MULTIPLICATEURS.MarqueConnue;
  if (ctx.saison === "Pic") m *= MULTIPLICATEURS.SaisonPic;
  if (ctx.saison === "Creuse") m *= MULTIPLICATEURS.SaisonCreuse;
  if (pr.active) m *= MULTIPLICATEURS.PromoActive;

  const budgetTheo = Math.min(budgetClicks, budgetTacos) * m;
  const { min, max } = CADRES_BUDGET[ctx.budgetStage];
  const budgetJour = borne(min, max, budgetTheo);

  const parts = CADRES_BUDGET[ctx.budgetStage].repartition;
  const budSP = budgetJour * parts.sp;
  const budSB = budgetJour * parts.sb;
  const budSD = budgetJour * parts.sd;

  // SP params
  const pSP = REP_SP[ctx.concurrence];
  const budExact = budSP * pSP.exact;
  const budPhrase = budSP * pSP.phrase;
  const budPT = budSP * pSP.product;
  const budAuto = budSP * pSP.auto;
  
  const kwExact = borne(8, 60, arrondi((budExact * 7) / (10 * Math.max(eco.cpcCat, 0.2))));
  const kwPhrase = borne(8, 60, arrondi((budPhrase * 7) / (10 * Math.max(eco.cpcCat, 0.2))));
  const asinPT = borne(10, 80, arrondi((budPT * 7) / (10 * Math.max(eco.cpcCat, 0.2))));
  const budParAG = borne(3, 25, budAuto / 4);
  const bidInit = eco.prixTTC * eco.cvrCat * eco.acosCible;
  const seuilClics = Math.max(20, arrondi(25 / Math.max(eco.cvrCat, 0.01)));

  // Lock reason
  let lockReason = "";
  if (ret.buyBoxPct < 0.92) {
    lockReason = "Buy box sous 92 %";
  } else if (ret.rating < 4.2 || ret.reviews < 50) {
    lockReason = "Preuve sociale insuffisante";
  } else if (ret.images < 6 || !ret.hasAplus) {
    lockReason = "Retail readiness incomplet";
  }

  return {
    clicks: arrondi(clicks),
    budgetClicks: arrondi(budgetClicks),
    budgetTacos: arrondi(budgetTacos),
    m,
    budgetTheo: arrondi(budgetTheo),
    budgetJour: arrondi(budgetJour),
    budSP: arrondi(budSP),
    budSB: arrondi(budSB),
    budSD: arrondi(budSD),
    bidInit,
    seuilClics,
    kwExact,
    kwPhrase,
    asinPT,
    budParAG: arrondi(budParAG),
    lockReason,
  };
};

// ============================================================================
// COMPOSANT
// ============================================================================

export const DebugPanel = ({
  contexte,
  optimizer,
  retail,
  placements,
  promo,
  daypart,
  reco,
  onContexteChange,
  onOptimizerChange,
  onPromoChange,
  onReset,
}: DebugPanelProps) => {
  const [showDebug, setShowDebug] = useState(false);
  
  const debug = computeDebug(contexte, optimizer, retail, promo);

  // Helpers pour colorer les badges
  const getBadgeVariant = (type: "good" | "warn" | "error"): "default" | "secondary" | "destructive" => {
    if (type === "good") return "default";
    if (type === "warn") return "secondary";
    return "destructive";
  };

  const isAlertActive = debug.lockReason !== "" || optimizer.stockJours < 15;
  const isWarnActive = placements.sosACoS > optimizer.acosCible && placements.sosCVR > 0.08;

  return (
    <Card className="border-2 border-dashed border-muted-foreground/50">
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg flex items-center gap-2">
            <Bug className="w-5 h-5" />
            Mode debug — Variables et tests
          </CardTitle>
          <div className="flex items-center gap-2">
            <Switch
              id="showDebug"
              checked={showDebug}
              onCheckedChange={setShowDebug}
            />
            <Label htmlFor="showDebug">Afficher le debug</Label>
          </div>
        </div>
      </CardHeader>
      
      {showDebug && (
        <CardContent className="space-y-6">
          {/* Bloc 1 — Budget calculs internes */}
          <div className="space-y-3">
            <h4 className="font-semibold text-sm">Budget — calculs internes</h4>
            <div className="flex flex-wrap gap-2">
              <Badge variant="outline">Clicks théoriques — {debug.clicks}</Badge>
              <Badge variant="outline">Budget clicks — {debug.budgetClicks}€</Badge>
              <Badge variant="outline">Budget TACoS — {debug.budgetTacos}€</Badge>
              <Badge variant={getBadgeVariant(debug.m < 1 ? "good" : "warn")}>
                Multiplicateur m — {debug.m.toFixed(2)}
              </Badge>
              <Badge variant="outline">Budget théorique — {debug.budgetTheo}€</Badge>
              <Badge variant="default">Budget borné — {debug.budgetJour}€</Badge>
            </div>
            <div className="flex flex-wrap gap-2 pt-2">
              <Badge variant="default">SP — {debug.budSP}€</Badge>
              <Badge variant="secondary">SB — {debug.budSB}€</Badge>
              <Badge variant="outline">SD — {debug.budSD}€</Badge>
            </div>
          </div>

          {/* Bloc 2 — SP paramètres dérivés */}
          <div className="space-y-3">
            <h4 className="font-semibold text-sm">SP — paramètres dérivés</h4>
            <div className="flex flex-wrap gap-2">
              <Badge variant="outline">Enchère initiale — {debug.bidInit.toFixed(2)}€</Badge>
              <Badge variant="outline">Seuil de clics — {debug.seuilClics}</Badge>
              <Badge variant="default">Mots clés Exact — {debug.kwExact}</Badge>
              <Badge variant="default">Mots clés Phrase — {debug.kwPhrase}</Badge>
              <Badge variant="default">ASIN Product Targeting — {debug.asinPT}</Badge>
              <Badge variant="secondary">Budget par ad group Auto — {debug.budParAG}€</Badge>
            </div>
          </div>

          {/* Bloc 3 — Verrous retail et placements */}
          <div className="space-y-3">
            <h4 className="font-semibold text-sm">Verrous retail et placements</h4>
            <div className="flex flex-wrap gap-2">
              <Badge variant={getBadgeVariant(retail.buyBoxPct >= 0.92 ? "good" : "error")}>
                Buy box — {(retail.buyBoxPct * 100).toFixed(0)}%
              </Badge>
              <Badge variant={getBadgeVariant(debug.lockReason === "" ? "good" : "error")}>
                Verrou — {debug.lockReason || "Aucun"}
              </Badge>
            </div>
            <div className="flex flex-wrap gap-2 pt-2">
              <Badge variant={getBadgeVariant(retail.rating >= 4.2 ? "good" : "warn")}>
                Note — {retail.rating.toFixed(1)}
              </Badge>
              <Badge variant={getBadgeVariant(retail.reviews >= 50 ? "good" : "warn")}>
                Avis — {retail.reviews}
              </Badge>
              <Badge variant={getBadgeVariant(retail.images >= 6 ? "good" : "warn")}>
                Images — {retail.images}
              </Badge>
              <Badge variant={getBadgeVariant(retail.hasAplus ? "good" : "warn")}>
                A+ — {retail.hasAplus ? "Oui" : "Non"}
              </Badge>
            </div>
            <div className="flex flex-wrap gap-2 pt-2">
              <Badge variant="outline">SoS ACoS — {(placements.sosACoS * 100).toFixed(0)}%</Badge>
              <Badge variant="outline">SoS CVR — {(placements.sosCVR * 100).toFixed(1)}%</Badge>
              <Badge variant="outline">RoS ACoS — {(placements.rosACoS * 100).toFixed(0)}%</Badge>
              <Badge variant="outline">PP ACoS — {(placements.ppACoS * 100).toFixed(0)}%</Badge>
            </div>
          </div>

          {/* Bloc 4 — Scénarios rapides */}
          <div className="space-y-3">
            <h4 className="font-semibold text-sm">Scénarios rapides — boutons</h4>
            <div className="flex flex-wrap gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => onContexteChange("concurrence", "BlueOcean")}
              >
                Blue Ocean
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => onContexteChange("marqueConnue", !contexte.marqueConnue)}
              >
                {contexte.marqueConnue ? "Désactiver" : "Activer"} marque connue
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => onContexteChange("budgetStage", "Minimal")}
              >
                Budget minimal
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => onOptimizerChange("stockJours", 10)}
              >
                Stock 10 jours
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  onOptimizerChange("cpcCat", 1.20);
                  setTimeout(() => onOptimizerChange("cvrCat", 0.05), 50);
                }}
              >
                CPC élevé et CVR bas
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => onPromoChange("active", !promo.active)}
              >
                {promo.active ? "Désactiver" : "Activer"} promo
              </Button>
              <Button
                variant="destructive"
                size="sm"
                onClick={onReset}
              >
                Réinitialiser
              </Button>
            </div>
          </div>

          {/* Bandeau info promo */}
          {promo.active && (
            <div className="flex items-start gap-2 p-4 bg-primary/10 border border-primary rounded-lg">
              <AlertCircle className="w-5 h-5 text-primary mt-0.5" />
              <p className="text-sm">
                <span className="font-semibold">Promo active —</span> ACoS cible relevée de 15 % — revert prévu 7 jours après fin
              </p>
            </div>
          )}
        </CardContent>
      )}
    </Card>
  );
};
